import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { PlusCircle, Loader2, RefreshCw, CheckCircle, Lock, X, Download } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import Modal from 'react-modal';
import { useData } from '../../contexts/DataContext';
import { api } from '../../utils/api';

interface CycleRow { id: number; status: string; start_date: string; end_date?: string; total_income?: string; total_expenses?: string; retained_fund?: string; opening_balance?: string | number; }

const modalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
        marginRight: '-50%', transform: 'translate(-50%, -50%)',
        backgroundColor: '#fff', border: 'none', padding: '2rem',
        borderRadius: '0.75rem', maxWidth: '560px', width: '90%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    overlay: {
        backgroundColor: 'rgba(15, 23, 42, 0.8)', zIndex: 50,
    },
};

export const AccountingCycleManager: React.FC<{ selectedCycleId: number | null; onSelectCycle: (id: number | null) => void; }> = ({ selectedCycleId, onSelectCycle }) => {
    const { cycles, allMovements, loading, refreshData, startNewCycle, closeCycle } = useData();

    const [isProcessing, setIsProcessing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cycleToCloseData, setCycleToCloseData] = useState({ id: 0, income: 0, expenses: 0, opening: 0 });
    const [distribution, setDistribution] = useState({ laika: '', murdok: '', murphy: '' });
    const [mercuryIds, setMercuryIds] = useState<{ laika: string[]; murdok: string[]; murphy: string[] }>({ laika: [], murdok: [], murphy: [] });
    const [mercuryLines, setMercuryLines] = useState<Array<{ partner: string; name: string; amount: number; date: string }>>([]);

    const handleStartNewCycle = async () => {
        if (!window.confirm('¿Estás seguro de que quieres iniciar un ciclo nuevo?')) return;

        setIsProcessing(true);
        const toastId = toast.loading('Iniciando nuevo ciclo...');
        try {
            const result = await startNewCycle();
            toast.success(result.message || 'Nuevo ciclo iniciado.', { id: toastId });
        } catch (error: any) {
            toast.error(`Error: ${error.message || 'No se pudo iniciar el ciclo.'}`, { id: toastId });
        } finally {
            setIsProcessing(false);
        }
    };

    const openCloseModal = (cycle: any) => {
        const cycleIncome = allMovements
            .filter(t => t.cycle_id === cycle.id && t.type === 'income')
            .reduce((sum, t) => sum + t.amount, 0);

        const cycleExpenses = allMovements
            .filter(e => e.cycle_id === cycle.id && e.type === 'expense')
            .reduce((sum, e) => sum + e.amount, 0);

        setCycleToCloseData({ id: cycle.id, income: cycleIncome, expenses: cycleExpenses, opening: parseFloat(cycle.opening_balance) || 0 });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setDistribution({ laika: '', murdok: '', murphy: '' });
        setMercuryIds({ laika: [], murdok: [], murphy: [] });
        setMercuryLines([]);
    };

    // Importa el reparto real desde Mercury desde el inicio del ciclo
    const handleImportReparto = async () => {
        if (cycleToCloseData.id === 0) return;
        setIsImporting(true);
        const toastId = toast.loading('Consultando reparto real en Mercury...');
        try {
            const result = await api.previewMercuryDistribution(cycleToCloseData.id);
            setDistribution({
                laika: result.laika.toFixed(2),
                murdok: result.murdok.toFixed(2),
                murphy: result.murphy.toFixed(2),
            });
            setMercuryIds({ laika: [], murdok: [], murphy: [] });
            const lines = result.lines || [];
            setMercuryLines(lines.map((l: any) => ({ partner: l.partner, name: l.name, amount: l.amount, date: l.date })));
            const ids: any = { laika: [], murdok: [], murphy: [] };
            for (const l of lines) if (ids[l.partner]) ids[l.partner].push(l.mercury_id);
            setMercuryIds(ids);
            toast.success(`Reparto importado: Laika ${result.laika.toFixed(2)} € · Angus ${result.murdok.toFixed(2)} € · Tokio ${result.murphy.toFixed(2)} €. Puedes ajustarlo antes de cerrar.`, { id: toastId, duration: 7000 });
        } catch (error: any) {
            toast.error(`Error importando de Mercury: ${error.message}`, { id: toastId, duration: 7000 });
        } finally {
            setIsImporting(false);
        }
    };

    const handleCloseCycleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (cycleToCloseData.id === 0) return;

        setIsProcessing(true);
        const toastId = toast.loading('Cerrando ciclo...');
        const dataToSubmit = {
            distributions: [
                { partner_name: 'LAIKA', amount: parseFloat(distribution.laika) || 0, mercury_id: mercuryIds.laika },
                { partner_name: 'MURDOK', amount: parseFloat(distribution.murdok) || 0, mercury_id: mercuryIds.murdok },
                { partner_name: 'MURPHY', amount: parseFloat(distribution.murphy) || 0, mercury_id: mercuryIds.murphy },
            ],
        };

        try {
            const result = await closeCycle(cycleToCloseData.id, dataToSubmit);
            toast.success(result.message || 'Ciclo cerrado con éxito.', { id: toastId, duration: 7000 });
            closeModal();
        } catch (error: any) {
            toast.error(`Error: ${error.message || 'No se pudo cerrar el ciclo.'}`, { id: toastId, duration: 7000 });
        } finally {
            setIsProcessing(false);
        }
    };

    const openCycle: CycleRow | undefined = (cycles as any[]).find((c: any) => c.status === 'OPEN');
    const netProfit = cycleToCloseData.income - cycleToCloseData.expenses;
    const available = cycleToCloseData.opening + netProfit;
    const totalDistributed = (parseFloat(distribution.laika) || 0) + (parseFloat(distribution.murdok) || 0) + (parseFloat(distribution.murphy) || 0);
    const remanent = available - totalDistributed;
    const isValidClose = remanent >= -0.01;

    return (
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
                <h3 className="text-xl font-bold text-gray-900">Gestión de Ciclos Contables</h3>
                <div className="flex items-center gap-4">
                    <button onClick={() => refreshData()} disabled={loading || isProcessing} className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 transition-colors" title="Actualizar lista">
                        <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <button onClick={handleStartNewCycle} disabled={isProcessing || !!openCycle} className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:bg-gray-400 flex items-center justify-center transition-colors">
                        {isProcessing ? <Loader2 className="animate-spin mr-2" /> : <PlusCircle className="w-5 h-5 mr-2" />}
                        Iniciar Nuevo Ciclo
                    </button>
                </div>
            </div>

            <div className="text-sm text-gray-600 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                {openCycle
                    ? `Ciclo #${openCycle.id} (desde ${formatDate(openCycle.start_date)}) · Saldo inicial: ${(parseFloat(String(openCycle.opening_balance ?? 0)) || 0).toFixed(2)} €`
                    : 'No hay ningún ciclo abierto. Inicia uno nuevo para empezar a registrar movimientos.'
                }
            </div>

            <div className="overflow-x-auto max-h-96">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Inicio</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Fin</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo Inicial</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ingresos</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gastos</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remanente</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr><td colSpan={9} className="text-center p-8"><Loader2 className="animate-spin inline-block text-blue-600" size={32}/></td></tr>
                        ) : (cycles as any[]).map((cycle: any) => (
                            <tr key={cycle.id} className={`cursor-pointer transition-colors ${selectedCycleId === cycle.id ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-gray-50'}`} onClick={() => onSelectCycle(selectedCycleId === cycle.id ? null : cycle.id)}>
                                <td className="px-6 py-4">
                                    {cycle.status === 'OPEN' && (
                                        <button onClick={(e) => { e.stopPropagation(); openCloseModal(cycle); }} className="bg-red-500 text-white text-xs font-bold py-1 px-3 rounded-md hover:bg-red-600">
                                            Cerrar
                                        </button>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">#{cycle.id}</td>
                                <td className="px-6 py-4 text-sm">
                                    {cycle.status === 'OPEN' ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-4 h-4 mr-1.5" /> Abierto</span>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><Lock className="w-4 h-4 mr-1.5" /> Cerrado</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">{formatDate(cycle.start_date)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{cycle.end_date ? formatDate(cycle.end_date) : 'N/A'}</td>
                                <td className="px-6 py-4 text-sm text-right text-indigo-600">{(parseFloat(String(cycle.opening_balance ?? 0)) || 0).toFixed(2)} €</td>
                                <td className="px-6 py-4 text-sm text-right text-green-600">{cycle.total_income ? `${parseFloat(cycle.total_income).toFixed(2)} €` : '—'}</td>
                                <td className="px-6 py-4 text-sm text-right text-red-600">{cycle.total_expenses ? `${parseFloat(cycle.total_expenses).toFixed(2)} €` : '—'}</td>
                                <td className="px-6 py-4 text-sm text-right text-blue-600">{cycle.retained_fund != null ? `${parseFloat(cycle.retained_fund).toFixed(2)} €` : 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal isOpen={isModalOpen} onRequestClose={closeModal} style={modalStyles} contentLabel="Cierre de Ciclo Contable" appElement={document.getElementById('root') || undefined}>
                 {cycleToCloseData.id > 0 && (
                     <form onSubmit={handleCloseCycleSubmit}>
                         <div className="flex justify-between items-center mb-4">
                             <h2 className="text-2xl font-bold text-gray-900">Cierre del Ciclo #{cycleToCloseData.id}</h2>
                             <button type="button" onClick={closeModal} className="p-2 rounded-full hover:bg-gray-200"><X size={20}/></button>
                         </div>
                         <div className="space-y-4 bg-gray-50 p-4 rounded-lg mb-6">
                             <div className="flex justify-between font-medium"><span className="text-gray-600">Saldo inicial:</span> <span className="text-indigo-600">{cycleToCloseData.opening.toFixed(2)} €</span></div>
                             <div className="flex justify-between font-medium"><span className="text-gray-600">Total Ingresos:</span> <span className="text-green-600">{cycleToCloseData.income.toFixed(2)} €</span></div>
                             <div className="flex justify-between font-medium"><span className="text-gray-600">Total Gastos:</span> <span className="text-red-600">{cycleToCloseData.expenses.toFixed(2)} €</span></div>
                             <hr/>
                             <div className="flex justify-between font-bold text-lg"><span className="text-gray-800">Total disponible:</span> <span>{available.toFixed(2)} €</span></div>
                         </div>

                         <button type="button" onClick={handleImportReparto} disabled={isImporting || isProcessing} className="w-full mb-6 bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center transition-colors">
                             {isImporting ? <Loader2 className="animate-spin mr-2" /> : <Download className="w-5 h-5 mr-2" />}
                             Importar reparto real de Mercury
                         </button>

                         {mercuryLines.length > 0 && (
                             <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
                                 <p className="font-semibold text-emerald-800 mb-2">Movimientos de Mercury detectados ({mercuryLines.length}):</p>
                                 <ul className="space-y-1 max-h-32 overflow-y-auto">
                                     {mercuryLines.map((l, i) => (
                                         <li key={i} className="flex justify-between text-gray-700">
                                             <span>{l.date} · {l.name}</span>
                                             <span className={l.amount < 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>{l.amount.toFixed(2)} $</span>
                                         </li>
                                     ))}
                                 </ul>
                             </div>
                         )}

                         <div className="space-y-4">
                             <div><label className="block text-sm font-medium text-gray-700">Pago a Laika</label><input type="number" step="0.01" value={distribution.laika} onChange={e => setDistribution({...distribution, laika: e.target.value})} className="mt-1 w-full p-2 border rounded-md" placeholder="0.00" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">Pago a Murdok (Angus)</label><input type="number" step="0.01" value={distribution.murdok} onChange={e => setDistribution({...distribution, murdok: e.target.value})} className="mt-1 w-full p-2 border rounded-md" placeholder="0.00" /></div>
                             <div><label className="block text-sm font-medium text-gray-700">Pago a Murphy (Tokio)</label><input type="number" step="0.01" value={distribution.murphy} onChange={e => setDistribution({...distribution, murphy: e.target.value})} className="mt-1 w-full p-2 border rounded-md" placeholder="0.00" /></div>
                         </div>

                         <div className={`mt-4 p-3 rounded-md ${isValidClose ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                             <div className="flex justify-between font-bold text-sm"><span>Repartido:</span><span>{totalDistributed.toFixed(2)} €</span></div>
                             <div className="flex justify-between font-bold text-base mt-1">
                                 <span>Remanente (saldo inicial del siguiente ciclo):</span>
                                 <span>{remanent.toFixed(2)} €</span>
                             </div>
                             {!isValidClose && <p className="text-xs mt-1 font-medium">El reparto supera el total disponible. Ajusta las cantidades.</p>}
                         </div>

                         <div className="flex justify-end space-x-4 mt-8">
                             <button type="button" onClick={closeModal} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300">Cancelar</button>
                             <button type="submit" disabled={isProcessing || !isValidClose} className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center">
                                 {isProcessing && <Loader2 className="animate-spin mr-2"/>}
                                 Confirmar y Cerrar Ciclo
                             </button>
                         </div>
                     </form>
                 )}
            </Modal>
        </div>
    );
};
